import React, {useEffect, useState} from "react";
import ReactDOM from "react-dom";
import {Provider} from "react-redux";
import classNames from "classnames/bind";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {WrappedIntlProvider} from "utils/intl";
import {store} from "store";
import dismissLightImg from "images/popup_dismiss_light.svg";
import styles from "./index.module.scss";
import TwoBtnPopUp from "./TwoBtnPopUp";
import OneBtnPopup from "./OneBtnPopup";

const cn = classNames.bind(styles);
function PopUpContainer(props) {
  const queryClient = new QueryClient();
  const [hasOverlay, setHasOverlay] = useState(false);
  const [hasContents, setHasContents] = useState(false);
  const overlay = document.getElementById("popup_overlay");
  const contents = document.getElementById("popup_contents");

  const mobileFullScreen = () => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  useEffect(() => {
    new ResizeObserver(() => {
      mobileFullScreen();
    }).observe(document.body);

    window.addEventListener("orientationchange", mobileFullScreen);
    return () => window.removeEventListener("orientationchange", mobileFullScreen);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setHasContents(contents === null);
      setHasOverlay(overlay === null);
    }, 10);
  }, []);

  const {
    dismiss,
    isOverlapPopup,
    dismissOverlay,
    defaultPadding,
    extraPadding,
    socialMode,
    closeEscButton,
    darkOverlay,
    clearOverHidden,
    transparentBackground,
    noBackground,
    tutorialSetting,
    kbPopupSetting,
    fullHeightPercent,
  } = props;

  useEffect(() => {
    window.addEventListener("keydown", closeWithESC);
    return () => window.removeEventListener("keydown", closeWithESC);
  }, []);

  const closeWithESC = e => {
    if (closeEscButton && e.code === "Escape") {
      dismiss();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className={`${styles.container} ${isOverlapPopup && styles.overlap_container} ${fullHeightPercent && styles.fullHeightPercent}`}>
        <div
          id="popup_overlay"
          className={cn(
            "overlay",
            isOverlapPopup && "overlay_overlap",
            !(isOverlapPopup || hasOverlay) && "overlay_invisible",
            darkOverlay && "darkOverlay",
          )}
          onClick={() => {
            if (dismissOverlay) dismiss();
          }}
        />
        <div id="popup_scrollable" className={styles.scrollable}>
          <div
            id="popup_contents"
            className={cn(
              "contents",
              !hasContents && "larged",
              defaultPadding && "defaultPadding",
              extraPadding && "extraPadding",
              socialMode && "socialMode",
              isOverlapPopup && "overlap_content",
              clearOverHidden && "clearOverflow",
              transparentBackground && "transparentBackground",
              noBackground && "noBackground",
              tutorialSetting && "tutorialSetting",
              kbPopupSetting && "kbPopupSetting",
              fullHeightPercent && "fullHeightPercent",
            )}
          >
            {props.children}
          </div>
        </div>
      </div>
    </QueryClientProvider>
  );
}

export const showPopUp = (content, options = {}) => {
  const {
    isOverlapPopup = false,
    dismissOverlay = false,
    defaultPadding = true,
    dismissButton = false,
    extraPadding = false,
    socialMode = false,
    closeEscButton = false,
    darkOverlay = false,
    clearOverHidden = false,
    transparentBackground = false,
    noBackground = false,
    tutorialSetting = false,
    kbPopupSetting = false,
    fullHeightPercent = false,
  } = options;

  const root = document.getElementById("root");
  let popup;
  if (isOverlapPopup) {
    popup = document.getElementById("overlap_popup");
  } else {
    popup = document.getElementById("popup");
  }

  if (!popup) {
    popup = document.createElement("div");
    popup.setAttribute("id", "popup");
    root.appendChild(popup);
    window.onhashchange = function () {
      showPopUp();
    };
  }

  const dismiss = () => {
    ReactDOM.render(null, popup);
    document.body.classList.remove("body-unscrollable");
  };
  dismiss();

  if (content) {
    setTimeout(() => {
      document.body.classList.add("body-unscrollable");
      ReactDOM.render(
        <Provider store={store}>
          <WrappedIntlProvider>
            <PopUpContainer
              dismiss={dismiss}
              isOverlapPopup={isOverlapPopup}
              dismissOverlay={dismissOverlay}
              defaultPadding={defaultPadding}
              extraPadding={extraPadding}
              socialMode={socialMode}
              closeEscButton={closeEscButton}
              darkOverlay={darkOverlay}
              clearOverHidden={clearOverHidden}
              transparentBackground={transparentBackground}
              noBackground={noBackground}
              tutorialSetting={tutorialSetting}
              kbPopupSetting={kbPopupSetting}
              fullHeightPercent={fullHeightPercent}
            >
              {dismissButton && (
                <img
                  id="popup_dismissbtn"
                  className={`${styles.dismissbtn} ${isOverlapPopup && styles.overlapbtn}`}
                  src={dismissLightImg}
                  alt="popup_dismissbtn"
                  onClick={dismiss}
                />
              )}
              {React.cloneElement(content, {dismiss})}
            </PopUpContainer>
          </WrappedIntlProvider>
        </Provider>,
        popup,
      );
    }, 10);
  }
};

export const hidePopUp = () => {
  let popup = document.getElementById("popup");
  document.body.classList.remove("body-unscrollable");
  const root = document.getElementById("root");
  root.removeChild(popup);
};

export {TwoBtnPopUp, OneBtnPopup};
